import { useEffect, useMemo, useRef, useState } from "react";

import { Search, X } from "lucide-react";

import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import { Input } from "@/components/ui/input";

import { useTableRows } from "../api";

/**
 * The "Data" tab: a paginated, read-only preview of a table's rows. Sorting,
 * paging AND search are server-driven (the row endpoint applies
 * LIMIT/OFFSET/ORDER BY and a cross-column ILIKE), so search matches the whole
 * table, not just the loaded page. Sensitive columns arrive pre-masked from the
 * backend and render muted.
 */

const PAGE_SIZE = 50;

type Row = Record<string, unknown> & { __rowid: string };

function formatValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground italic">null</span>;
  }
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") {
    return <span className="font-mono text-xs">{JSON.stringify(value)}</span>;
  }
  return String(value);
}

export function TableDataView({ schema, table }: { schema: string; table: string }) {
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<DataTableSort | null>(null);

  // Debounce the search box so we don't fire a query per keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const firstRun = useRef(true);
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput);
      if (!firstRun.current) setOffset(0);
      firstRun.current = false;
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading, isError, error } = useTableRows(
    schema,
    table,
    {
      limit: PAGE_SIZE,
      offset,
      orderBy: sort?.key ?? null,
      dir: sort?.dir ?? "asc",
      search,
    },
    true,
  );

  const masked = useMemo(
    () => new Set(data?.masked_columns ?? []),
    [data?.masked_columns],
  );

  const columns: DataTableColumn<Row>[] = useMemo(
    () =>
      (data?.columns ?? []).map((name) => ({
        key: name,
        header: name,
        sortable: true,
        render: (row: Row) =>
          masked.has(name) ? (
            <span className="font-mono text-muted-foreground">{String(row[name])}</span>
          ) : (
            <div className="max-w-[28rem] truncate" title={cellTitle(row[name])}>
              {formatValue(row[name])}
            </div>
          ),
      })),
    [data?.columns, masked],
  );

  const rows: Row[] = useMemo(
    () =>
      (data?.rows ?? []).map((r, i) => ({ ...r, __rowid: `${offset + i}` })),
    [data?.rows, offset],
  );

  function onSortChange(next: DataTableSort | null) {
    setSort(next);
    setOffset(0);
  }

  const searchBox = (
    <div className="relative w-64 max-w-full">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder="Search this table…"
        className="h-9 px-8"
      />
      {searchInput ? (
        <button
          type="button"
          onClick={() => setSearchInput("")}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.__rowid}
      sort={sort}
      onSortChange={onSortChange}
      toolbar={searchBox}
      pagination={{ limit: PAGE_SIZE, offset, count: data?.total ?? 0 }}
      onPageChange={setOffset}
      loading={isLoading}
      error={isError ? (error as Error)?.message ?? "Failed to load rows." : null}
      emptyMessage={search ? "No rows match your search." : "This table is empty."}
      containerClassName="max-h-[calc(100vh-26rem)]"
    />
  );
}

function cellTitle(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
